import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// ── Theme Definitions ──────────────────────────────────────────────
export interface ThemeColors {
    ivory: string;
    ivoryDeep: string;
    ivoryDark: string;
    gold: string;
    goldLight: string;
    goldPale: string;
    goldBorder: string;
    goldShadow: string;
    charcoal: string;
    charcoalSoft: string;
    charcoalMuted: string;
    charcoalFaint: string;
    primaryGradient: string;
    secondaryGradient: string;
    successColor: string;
    warningColor: string;
    errorColor: string;
    infoColor: string;
    glassBackground: string;
    glassBorder: string;
}

export interface ThemeMeta {
    id: string;
    name: string;
    emoji: string;
    description: string;
    previewColors: string[];   // 4-5 preview swatch colors for the picker UI
    colors: ThemeColors;
}

export const THEMES: ThemeMeta[] = [
    {
        id: 'royal-gold',
        name: 'Royal Gold',
        emoji: '👑',
        description: 'Classic heritage warmth — ivory & antique gold',
        previewColors: ['#F9F5F1', '#C5A059', '#3E1F24', '#A8844A', '#F0EAE2'],
        colors: {
            ivory: '#F9F5F1',
            ivoryDeep: '#F0EAE2',
            ivoryDark: '#E8DDD2',
            gold: '#C5A059',
            goldLight: '#D4B47A',
            goldPale: 'rgba(197, 160, 89, 0.12)',
            goldBorder: 'rgba(197, 160, 89, 0.35)',
            goldShadow: 'rgba(197, 160, 89, 0.25)',
            charcoal: '#333333',
            charcoalSoft: '#555555',
            charcoalMuted: '#888888',
            charcoalFaint: '#BBBBBB',
            primaryGradient: 'linear-gradient(135deg, #C5A059 0%, #A8844A 50%, #C5A059 100%)',
            secondaryGradient: 'linear-gradient(135deg, #E8DDD2 0%, #F9F5F1 100%)',
            successColor: '#4CAF50',
            warningColor: '#C5A059',
            errorColor: '#E57373',
            infoColor: '#6B93C4',
            glassBackground: 'rgba(255, 255, 255, 0.75)',
            glassBorder: 'rgba(197, 160, 89, 0.3)',
        },
    },
    {
        id: 'rose-blush',
        name: 'Rose Blush',
        emoji: '🌸',
        description: 'Romantic soft pinks with rose-gold shimmer',
        previewColors: ['#FFF5F7', '#D4728C', '#4A1942', '#E8A0BF', '#F5E6CC'],
        colors: {
            ivory: '#FFF5F7',
            ivoryDeep: '#FCE4EC',
            ivoryDark: '#F8BBD0',
            gold: '#D4728C',
            goldLight: '#E8A0BF',
            goldPale: 'rgba(212, 114, 140, 0.12)',
            goldBorder: 'rgba(212, 114, 140, 0.30)',
            goldShadow: 'rgba(212, 114, 140, 0.22)',
            charcoal: '#3D2030',
            charcoalSoft: '#5C3D52',
            charcoalMuted: '#9E7A8F',
            charcoalFaint: '#C8A8BC',
            primaryGradient: 'linear-gradient(135deg, #D4728C 0%, #B8527A 50%, #D4728C 100%)',
            secondaryGradient: 'linear-gradient(135deg, #FCE4EC 0%, #FFF5F7 100%)',
            successColor: '#66BB6A',
            warningColor: '#FFB74D',
            errorColor: '#EF5350',
            infoColor: '#7986CB',
            glassBackground: 'rgba(255, 245, 247, 0.80)',
            glassBorder: 'rgba(212, 114, 140, 0.25)',
        },
    },
    {
        id: 'midnight-sapphire',
        name: 'Midnight Sapphire',
        emoji: '💎',
        description: 'Bold dark elegance — deep navy & electric teal',
        previewColors: ['#0D1B2A', '#1B998B', '#48C9B0', '#1A365D', '#162447'],
        colors: {
            ivory: '#0D1B2A',
            ivoryDeep: '#162447',
            ivoryDark: '#1A365D',
            gold: '#1B998B',
            goldLight: '#48C9B0',
            goldPale: 'rgba(27, 153, 139, 0.15)',
            goldBorder: 'rgba(27, 153, 139, 0.35)',
            goldShadow: 'rgba(27, 153, 139, 0.25)',
            charcoal: '#E0F2F1',
            charcoalSoft: '#B2DFDB',
            charcoalMuted: '#80CBC4',
            charcoalFaint: '#4DB6AC',
            primaryGradient: 'linear-gradient(135deg, #1B998B 0%, #0F7A6E 50%, #1B998B 100%)',
            secondaryGradient: 'linear-gradient(135deg, #162447 0%, #0D1B2A 100%)',
            successColor: '#66BB6A',
            warningColor: '#FFD54F',
            errorColor: '#FF7043',
            infoColor: '#4FC3F7',
            glassBackground: 'rgba(13, 27, 42, 0.85)',
            glassBorder: 'rgba(27, 153, 139, 0.30)',
        },
    },
];

// ── Context ────────────────────────────────────────────────────────
interface ThemeContextType {
    currentTheme: ThemeMeta;
    setTheme: (themeId: string) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'shadigram-theme';

function applyThemeToDOM(theme: ThemeMeta) {
    const root = document.documentElement;
    const c = theme.colors;

    root.style.setProperty('--ivory', c.ivory);
    root.style.setProperty('--ivory-deep', c.ivoryDeep);
    root.style.setProperty('--ivory-dark', c.ivoryDark);
    root.style.setProperty('--gold', c.gold);
    root.style.setProperty('--gold-light', c.goldLight);
    root.style.setProperty('--gold-pale', c.goldPale);
    root.style.setProperty('--gold-border', c.goldBorder);
    root.style.setProperty('--gold-shadow', c.goldShadow);
    root.style.setProperty('--charcoal', c.charcoal);
    root.style.setProperty('--charcoal-soft', c.charcoalSoft);
    root.style.setProperty('--charcoal-muted', c.charcoalMuted);
    root.style.setProperty('--charcoal-faint', c.charcoalFaint);
    root.style.setProperty('--primary-gradient', c.primaryGradient);
    root.style.setProperty('--primary-color', c.gold);
    root.style.setProperty('--secondary-gradient', c.secondaryGradient);
    root.style.setProperty('--theme-gradient', c.primaryGradient);
    root.style.setProperty('--theme-glow', c.goldShadow);
    root.style.setProperty('--success-color', c.successColor);
    root.style.setProperty('--warning-color', c.warningColor);
    root.style.setProperty('--error-color', c.errorColor);
    root.style.setProperty('--info-color', c.infoColor);
    root.style.setProperty('--glass-bg', c.glassBackground);
    root.style.setProperty('--glass-border', c.glassBorder);

    // Derived aliases
    root.style.setProperty('--text-primary', c.charcoal);
    root.style.setProperty('--text-secondary', c.charcoalSoft);
    root.style.setProperty('--text-muted', c.charcoalMuted);
    root.style.setProperty('--text-light', c.charcoalFaint);
    root.style.setProperty('--background', c.ivory);
    root.style.setProperty('--surface', theme.id === 'midnight-sapphire' ? '#162447' : '#FFFFFF');
    root.style.setProperty('--surface-hover', c.ivoryDeep);
    root.style.setProperty('--surface-card', theme.id === 'midnight-sapphire' ? 'rgba(22, 36, 71, 0.82)' : 'rgba(255, 255, 255, 0.82)');
    root.style.setProperty('--border', c.goldBorder);
    root.style.setProperty('--border-subtle', theme.id === 'midnight-sapphire' ? 'rgba(27, 153, 139, 0.18)' : c.goldBorder.replace('0.35', '0.18'));
    root.style.setProperty('--shadow-btn', `0 4px 12px ${c.goldShadow}`);
    root.style.setProperty('--gold-border-soft', c.goldBorder.replace('0.35', '0.20'));

    // Set data attribute for possible CSS-only selectors
    root.setAttribute('data-theme', theme.id);
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentTheme, setCurrentThemeState] = useState<ThemeMeta>(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        const found = THEMES.find(t => t.id === saved);
        return found || THEMES[0];
    });

    useEffect(() => {
        applyThemeToDOM(currentTheme);
    }, [currentTheme]);

    const setTheme = (themeId: string) => {
        const theme = THEMES.find(t => t.id === themeId);
        if (theme) {
            setCurrentThemeState(theme);
            localStorage.setItem(STORAGE_KEY, themeId);
        }
    };

    return (
        <ThemeContext.Provider value={{ currentTheme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
